import { useTranslation } from 'react-i18next';
import { AlgComponentType, IncompatibilityRules } from '@youshift/shared/types';
import { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';

import ImportanceInput from './ImportanceInput';
import { getImportanceLevel, getImportanceValue, isUnbreakable } from './utils';
import InfoIcon from '../InfoIcon';

interface IncompatibilitiesPriorityProps {
  incompatibilities: IncompatibilityRules;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
}

export default function IncompatibilitiesPriority({
  incompatibilities,
  importanceValues,
  setImportanceValues,
}: IncompatibilitiesPriorityProps) {
  const { t } = useTranslation();

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-medium">{t('generic.incompatibilities')}</h3>
        <InfoIcon
          title={t(
            'manager.smartass.algorithmSettingsModal.incompatibilityPriority.title',
          )}
          content={
            <>
              <p className="pt-4">
                {t(
                  'manager.smartass.algorithmSettingsModal.incompatibilityPriority.description',
                )}
              </p>
              <p className="mt-2 font-medium">
                {t('manager.smartass.algorithmSettingsModal.constraintCount', {
                  count:
                    Object.values(
                      importanceValues[
                        AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MIN
                      ],
                    ).length +
                    Object.values(
                      importanceValues[
                        AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MAX
                      ],
                    ).length +
                    Object.values(
                      importanceValues[
                        AlgComponentType.CROSS_GROUP_INCOMPATIBILITY
                      ],
                    ).length,
                })}
              </p>
            </>
          }
          className="w-4 h-4"
          iconSize={6}
          size="2xl"
        />
      </div>
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium mb-2">
            {t('manager.incomp.singleGroup')}
          </h4>
          <div className="space-y-2">
            {Object.entries(incompatibilities.single_group_incomp).map(
              ([id, incompatibility]) => (
                <div key={id} className="border rounded">
                  <div className="grid grid-cols-[1fr,2fr] w-full p-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {incompatibility.name}
                      </span>
                    </div>
                    <div className="flex justify-center">
                      <table className="border-collapse">
                        <tbody>
                          <tr>
                            <td className="pr-2">
                              <span className="text-sm text-gray-600">
                                min:
                              </span>
                            </td>
                            <td className="pr-2">
                              <ImportanceInput
                                value={
                                  importanceValues[
                                    AlgComponentType
                                      .SINGLE_GROUP_INCOMPATIBILITY_MIN
                                  ][Number(id)] ?? 0
                                }
                                onChange={value => {
                                  setImportanceValues(prev => ({
                                    ...prev,
                                    [AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MIN]:
                                      {
                                        ...prev[
                                          AlgComponentType
                                            .SINGLE_GROUP_INCOMPATIBILITY_MIN
                                        ],
                                        [Number(id)]: value,
                                      },
                                  }));
                                }}
                              />
                            </td>
                            <td></td>
                          </tr>
                          <tr>
                            <td className="pr-2">
                              <span className="text-sm text-gray-600">
                                max:
                              </span>
                            </td>
                            <td className="pr-2">
                              <ImportanceInput
                                value={
                                  importanceValues[
                                    AlgComponentType
                                      .SINGLE_GROUP_INCOMPATIBILITY_MAX
                                  ][Number(id)] ?? 0
                                }
                                onChange={value => {
                                  setImportanceValues(prev => ({
                                    ...prev,
                                    [AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MAX]:
                                      {
                                        ...prev[
                                          AlgComponentType
                                            .SINGLE_GROUP_INCOMPATIBILITY_MAX
                                        ],
                                        [Number(id)]: value,
                                      },
                                  }));
                                }}
                              />
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
        <div>
          <h4 className="text-sm font-medium mb-2">
            {t('manager.incomp.crossGroup')}
          </h4>
          <div className="space-y-2">
            {Object.entries(incompatibilities.cross_group_incomp).map(
              ([id, incompatibility]) => (
                <div key={id} className="border rounded">
                  <div className="grid grid-cols-[1fr,2fr] w-full p-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {incompatibility.name}
                      </span>
                    </div>
                    <div className="flex justify-center">
                      <table className="border-collapse">
                        <tbody>
                          <tr>
                            <td className="pr-2">
                              <span className="text-sm text-gray-600">
                                value:
                              </span>
                            </td>
                            <td className="pr-2">
                              <ImportanceInput
                                value={
                                  importanceValues[
                                    AlgComponentType.CROSS_GROUP_INCOMPATIBILITY
                                  ][Number(id)] ?? 0
                                }
                                onChange={value => {
                                  setImportanceValues(prev => ({
                                    ...prev,
                                    [AlgComponentType.CROSS_GROUP_INCOMPATIBILITY]:
                                      {
                                        ...prev[
                                          AlgComponentType
                                            .CROSS_GROUP_INCOMPATIBILITY
                                        ],
                                        [Number(id)]: value,
                                      },
                                  }));
                                }}
                              />
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
